import classNames from "classnames";
import React, { PropTypes } from "react";
import { ButtonToolbar } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { SubmissionError } from "redux-form";

import { ArchiveButton, MessageBox, CheckboxButton, EditButton, Render } from "../../../../common";
import { DataTable } from "../../../../common/DataTable";
import { BasicDivisionFields } from "./";

import * as types from "../../../../../utils/data/dataActionTypes";
import * as dataActions from "../../../../../utils/data/dataActions";
import { DIVISIONS } from "../../../../../utils/data/models";

import { toast } from "react-toastify";
import {
    AGENT,
    DIRECTOR,
    FINANCE,
    FINTECH,
    HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER,
} from "../../../../../utils/Roles";
import userHasAccess from "../../../../../utils/userHasAccess";
import { RectangleSpinner } from "../../../../common/spinners";
import { canViewSyncForTEDDesjardins, canViewSyncStatus, getLatestDivisionSyncs, getOverlayForSync } from "../../../../common/synchronization/SynchronizationHelper";
import { SUCCESS } from "../../../synchronization/enums/syncStatusTypes";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../../enums/clientStatusTypes";
import { DANGER, SUCCESS as SUCCESS_STATUS, WARNING } from "../../enums/messageBoxTypes";
import _ from "lodash";
import { divisionsErrors } from "../../../../../utils/enums/enumDivisionsErrors";
import { DIVISION } from "../../../synchronization/enums/syncElementTypes";

class DivisionsDataTable extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            latestSyncs: null,
            syncStatus: "",
            divisionSyncDictionary: {},
        };
        this.archiveDivision = this.archiveDivision.bind(this);
        this.createDivision = this.createDivision.bind(this);
        this.setCellClassName = this.setCellClassName.bind(this);
        this.toggleArchivedDivisions = this.toggleArchivedDivisions.bind(this);
        this.updateDivision = this.updateDivision.bind(this);
        this.getData = this.getData.bind(this);
        this.getViewOnlyForDivisions = this.getViewOnlyForDivisions.bind(this);
        this.getDivisionSyncByDivision = this.getDivisionSyncByDivision.bind(this);
        this.viewOnlyForImplementation = !userHasAccess(FINANCE);
        this.viewOnlyForActive = !userHasAccess(DIRECTOR);
        this.viewOnlyForTerminated = !userHasAccess(FINTECH);
        this.hasValidRoleForSync = userHasAccess(HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER, true);

        this.shouldDisplaySync = canViewSyncStatus(this.props.activeClient, DIVISION);
        this.shouldDisplaySyncForTEDDesjardins = canViewSyncForTEDDesjardins(this.props.activeClient);
    }

    componentWillMount() {
        this.props.dataActions.clearDataList(DIVISIONS);
    }

    componentWillReceiveProps(nextProps) {
        // Reload data when clientId changes
        if (nextProps.clientId !== this.props.clientId) {
            this.props.dataActions.clearDataList(DIVISIONS);
            this.getData(
                this.props.offset,
                this.props.limit,
                this.props.q,
                this.props.sortBy,
                nextProps.filters || this.props.filters
            );
        }
    }

    getViewOnlyForDivisions() {
        const { clientStatusType } = this.props;

        switch (clientStatusType) {
            case IMPLEMENTATION.value:
                return this.viewOnlyForImplementation;
            case ACTIVE.value:
            case INACTIVE.value:
                return this.viewOnlyForActive;
            case TERMINATED.value:
                return this.viewOnlyForTerminated;
        }
    }

    getDivisionSyncByDivision(division) {
        const divisionSyncs = this.state.divisionSyncDictionary;
        if (divisionSyncs && Object.keys(divisionSyncs).length > 0) {
            return divisionSyncs[division.divisionId] || {};
        }
        return {};
    }

    archiveDivision(divisionId, isArchived) {
        this.props.dataActions
            .updateDataItem(DIVISIONS, { divisionId, isArchived: !isArchived, clientId: this.props.clientId })
            .then((data) => {
                if (data.type === types.UPDATE_DATA_ITEM_FAILURE) {
                    const error = data.payload.error.data.error;
                    const message = data.payload.error.data.message;

                    if (message === "pastLastActiveDateError") {
                        toast(<FormattedMessage id="pastLastActiveDateError" />, {
                            type: toast.TYPE.ERROR,
                            autoClose: false,
                        });
                    } else if (divisionsErrors.archive.includes(error)) {
                        toast(<FormattedMessage id={`divisions.errors.${error}`} />, {
                            type: toast.TYPE.ERROR,
                            autoClose: false,
                        });
                    } else {
                        toast(<FormattedMessage id="validation.customMessages.genericError" />, {
                            type: toast.TYPE.ERROR,
                            autoClose: false,
                        });
                    }
                } else {
                    this.getData(
                        this.props.offset,
                        this.props.limit,
                        this.props.q,
                        this.props.sortBy,
                        this.props.filters
                    );
                }
            });
    }

    createDivision(values) {
        return this.props.dataActions.createDataItem(DIVISIONS, values).then((data) => {
            if (
                data.type === types.CREATE_DATA_ITEM_FAILURE &&
                data.payload.error.data.message === "pastLastActiveDateError"
            ) {
                throw new SubmissionError({
                    name: {
                        messageKey: "pastLastActiveDateError",
                        values: {},
                        state: "error",
                    },
                });
            } else {
                this.getData(
                    this.props.offset,
                    this.props.limit,
                    this.props.q,
                    this.props.sortBy,
                    this.props.filters,
                    data.type
                );
            }
        });
    }

    setCellClassName(division) {
        return classNames({
            archived: division.isArchived,
        });
    }

    toggleArchivedDivisions() {
        const isArchivedFiltered = this.props.filters.includes(DIVISIONS.archiveFilter);
        const filters = isArchivedFiltered
            ? this.props.filters.filter((filter) => filter !== DIVISIONS.archiveFilter)
            : [...this.props.filters, DIVISIONS.archiveFilter];
        this.getData(this.props.offset, this.props.limit, this.props.q, this.props.sortBy, filters);
    }

    updateDivision(values) {
        const { divisionId, name, divisionNumber, externalDivisionNumber, provinceCode } = values;
        const dataToUpdate = {
            divisionId,
            name,
            divisionNumber,
            externalDivisionNumber,
            clientId: this.props.clientId,
            provinceCode
        };

        return this.props.dataActions.updateDataItem(DIVISIONS, dataToUpdate).then((data) => {
            if (
                data.type === types.UPDATE_DATA_ITEM_FAILURE &&
                data.payload.error.data.message === "pastLastActiveDateError"
            ) {
                throw new SubmissionError({
                    name: {
                        messageKey: "pastLastActiveDateError",
                        values: {},
                        state: "error",
                    },
                });
            } else {
                this.getData(
                    this.props.offset,
                    this.props.limit,
                    this.props.q,
                    this.props.sortBy,
                    this.props.filters,
                    data.type
                );
            }
        });
    }

    getCols() {
        const cols = [
            {
                name: <FormattedMessage id="divisions.datatable.columns.division" />,
                key: "name",
                display: (division) => <span className={this.setCellClassName(division)}>{division.name}</span>,
                widthPct: this.shouldDisplaySync ? 30 : this.shouldDisplaySyncForTEDDesjardins ? 55 : 80,
            },
        ];
        if (this.shouldDisplaySync || this.shouldDisplaySyncForTEDDesjardins) {
            cols.push(
                {
                    name: <FormattedMessage id="divisions.datatable.columns.externalDivisionNumber" />,
                    key: "externalDivisionNumber",
                    display: (division) => {
                        return (
                            <span className={this.setCellClassName(division)}>{division.externalDivisionNumber}</span>
                        );
                    },
                    widthPct: 25,
                },
            );
        }
        this.shouldDisplaySync &&
            cols.push({
                name: <FormattedMessage id="divisions.datatable.columns.sync" />,
                display: (division) => {
                    const { latestSyncs } = this.state;
                    if (!latestSyncs) {
                        return <RectangleSpinner className="spinner-rect-col" />;
                    }

                    const latestDivisionSync = latestSyncs.find((e) => e.divisionId === division.divisionId);
                    const data = { "division": _.cloneDeep(division), "client": this.props.activeClient };
                    return getOverlayForSync(
                        data,
                        latestDivisionSync,
                        "divisions.datatable",
                        "divisionPartnerHash",
                        "divisionHash",
                        "division"
                    );
                },
                widthPct: 25,
            });
        cols.push({
            name: <FormattedMessage id="datatable.actions" />,
            display: (division) => this.generateActionButtons(division),
            widthPct: 20,
        });
        return cols;
    }

    renderFormatedMessage(icon) {
        return <span className="icon warning">{icon}</span>;
    }
    generateActionButtons(division) {
        let viewOnly = this.getViewOnlyForDivisions() || this.props.isArchived;

        return (
            <ButtonToolbar key={division.divisionId}>
                <EditButton
                    title={<FormattedMessage id={`divisions.datatable.actions.${viewOnly ? "view" : "edit"}`} />}
                    label={<FormattedMessage id={viewOnly ? "common.view" : "common.edit"} />}
                    confirmationTitle={
                        <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
                            {this.renderFormatedMessage}
                        </FormattedMessage>
                    }
                    confirmationMessage={<FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />}
                    form={BasicDivisionFields}
                    allowSubmission={!viewOnly}
                    props={{
                        viewOnly,
                        disabled: viewOnly,
                        syncStatus: this.shouldDisplaySync ? this.getDivisionSyncByDivision(division) : "",
                        clientCountryCode: this.props.activeClient.countryCode,
                        syncType: this.props.clientSyncType
                    }}
                    data={division}
                    editFunction={this.updateDivision}
                    requiresCloseConfirmation={true}
                    confirmOnClose={true}
                />
                {!viewOnly && (
                    <Render for={AGENT} if={!division.isArchived}>
                        <ArchiveButton
                            showDefaultDanger={true}
                            isArchived={division.isArchived}
                            archivedName={division.name}
                            archiveFunction={this.archiveDivision.bind(null, division.divisionId, division.isArchived)}
                            label={
                                division.isArchived ? (
                                    <FormattedMessage id="common.unarchive" />
                                ) : (
                                    <FormattedMessage id="common.archive" />
                                )
                            }
                        />
                    </Render>
                )}
            </ButtonToolbar>
        );
    }

    generateAddButton(clientId) {
        const viewOnly = this.getViewOnlyForDivisions();
        return (
            <div>
                <CheckboxButton
                    className="include-archived"
                    id="archived"
                    label={<FormattedMessage id="divisions.datatable.actions.includeArchivedDivisions" />}
                    checked={!this.props.filters.includes(DIVISIONS.archiveFilter)}
                    onClick={this.toggleArchivedDivisions}
                />
                {!viewOnly && (
                    <Render for={AGENT} if={this.props.clientStatusType != TERMINATED.value && !this.props.isArchived}>
                        <EditButton
                            title={<FormattedMessage id="divisions.datatable.actions.add" />}
                            label={<FormattedMessage id="common.add" />}
                            add={true}
                            form={BasicDivisionFields}
                            data={{ clientId }}
                            editFunction={this.createDivision}
                            props={{
                                viewOnly, disabled: viewOnly, clientCountryCode: this.props.activeClient.countryCode,
                                clientProvinceCode: this.props.activeClient.provinceCode, syncType: this.props.clientSyncType
                            }}
                            confirmationTitle={
                                <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
                                    {this.renderFormatedMessage}
                                </FormattedMessage>
                            }
                            confirmationMessage={
                                <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />
                            }
                            closeButtonKey="common.cancel"
                            requiresCloseConfirmation={true}
                            confirmOnClose={true}
                        />
                    </Render>
                )}
            </div>
        );
    }

    getData(offset, limit, q, sortBy, filters, actionType = "") {
        switch (actionType) {
            case types.CREATE_DATA_ITEM_FAILURE:
            case types.UPDATE_DATA_ITEM_FAILURE: {
                throw new SubmissionError({
                    name: {
                        messageKey: "validation.customMessages.serverErrors.duplicateDivisionName",
                        values: {},
                        state: "error",
                    },
                });
            }
            default: {
                return this.props.dataActions
                    .getDataList(DIVISIONS, offset, limit, q, sortBy, filters, null, {
                        clientId: this.props.clientId,
                    })
                    .then((res) => {
                        const divisionIds = res.payload.data ? res.payload.data.items.map((m) => m.divisionId) : [];
                        return (
                            this.shouldDisplaySync &&
                            getLatestDivisionSyncs(this.props.clientId, divisionIds)
                                .then((response) => {
                                    const latestSyncs = response;
                                    this.setState({ latestSyncs });
                                    if (divisionIds.length > 0) {
                                        let syncStatus = DANGER;
                                        let syncDictionary = {};

                                        if (Array.isArray(latestSyncs) && latestSyncs.length > 0) {
                                            const successSyncs = latestSyncs.filter(
                                                (sync) =>
                                                    divisionIds.includes(sync.divisionId) && sync.status === SUCCESS
                                            );

                                            divisionIds.forEach((id) => {
                                                syncDictionary[id] =
                                                    latestSyncs.find((sync) => sync.divisionId === id) || null;
                                            });

                                            if (successSyncs.length > 0) {
                                                syncStatus =
                                                    successSyncs.length === divisionIds.length
                                                        ? SUCCESS_STATUS
                                                        : WARNING;
                                            }
                                        }

                                        this.setState({
                                            syncStatus: syncStatus,
                                            divisionSyncDictionary: syncDictionary,
                                        });
                                    }
                                })
                                .catch(() => {
                                    toast(
                                        <FormattedMessage id="validation.customMessages.serverErrors.genericError" />,
                                        {
                                            type: toast.TYPE.ERROR,
                                            autoClose: false,
                                        }
                                    );
                                })
                        );
                    })
                    .catch(() => {
                        toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                            type: toast.TYPE.ERROR,
                            autoClose: false,
                        });
                    });
            }
        }
    }

    render() {
        return (
            <div>
                {this.shouldDisplaySync && this.state.syncStatus && (
                    <MessageBox className={`alert-${this.state.syncStatus}  synchronization-message-box-datatable`}>
                        <span className="d-flex">
                            <FormattedMessage id={`divisions.datatable.syncDisplayStatus.${this.state.syncStatus}`} />
                        </span>
                    </MessageBox>
                )}

                <DataTable
                    ref="dataTable"
                    title={<FormattedMessage id="divisions.title" />}
                    getData={this.getData}
                    cols={this.getCols()}
                    q={this.props.q}
                    filters={this.props.filters}
                    offset={this.props.offset}
                    data={this.props.items}
                    rowsPerPage={this.props.limit}
                    totalRows={this.props.total}
                    sortBy={this.props.sortBy}
                    addButton={this.generateAddButton(this.props.clientId)}
                    refreshFunction={this.getData}
                />
            </div>
        );
    }
}

DivisionsDataTable.propTypes = {
    dataActions: PropTypes.object,
    filters: PropTypes.array,
    q: PropTypes.string,
    items: PropTypes.array,
    limit: PropTypes.number,
    offset: PropTypes.number,
    sortBy: PropTypes.array,
    total: PropTypes.number,
    clientId: PropTypes.number,
    statuses: PropTypes.array,
    insuranceTypes: PropTypes.array,
    activeClient: PropTypes.object,
    clientStatusType: PropTypes.string,
    isArchived: PropTypes.bool,
    clientSyncType: PropTypes.string
};

function mapStateToProps(state) {
    const data = state.data.divisions || {};
    const newClient = data.clientId != state.data.clients.active.clientId;
    const pagination = data.pagination || {};
    return {
        activeClient: state.data.clients.active,
        q: data.q,
        filters: newClient
            ? [`clientId==${state.data.clients.active.clientId}`, DIVISIONS.archiveFilter]
            : data.filters,
        clientId: state.data.clients.active.clientId,
        clientStatusType: state.data.clients.active.clientStatusType,
        clientSyncType: state.data && state.data.clients && state.data.clients.active && state.data.clients.active.syncType,
        limit: pagination.limit || 100,
        offset: pagination.offset || 0,
        sortBy: data.sortBy || ["name:asc"],
        total: pagination.totalCount || 0,
        items: data.items || null,
        isArchived: state.data.clients.active.isArchived,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dataActions: bindActionCreators(dataActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(DivisionsDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/divisions/DivisionsDataTable.js